import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  Box,
  Divider,
  CircularProgress,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMap } from '@vis.gl/react-google-maps';

const StyledPaper = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '80px',
  right: '20px',
  padding: theme.spacing(2),
  minWidth: '250px',
  zIndex: 1000,
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  borderRadius: '12px',
  [theme.breakpoints.down('sm')]: {
    top: '62px',
    right: '10px',
    minWidth: 'auto',
    maxWidth: 'none',
    width: 'calc(100vw - 270px)', // Changed from 311px to 270px for wider mobile card
  },
}));

const DemographicsNavigator = ({ 
  visible, 
  selectedDemographic = 'black_percentage', 
  isActive,
  isLoading,
  clickedPolygonInfo,
  onGoCountyLevel,
  onGoBlockGroupLevel,  // Add this new prop
  onGoBack,
  selectedGeoLevel
}) => {
  // Get map instance
  const mapInstance = useMap();
  
  // Watch for prop changes and control map zoom
  useEffect(() => {
    if (!mapInstance || !isActive) return;
    // Only change zoom when geographic level changes (navigation buttons clicked)
    // Don't change zoom when just clicking on a polygon
    if (selectedGeoLevel === 'nationwide') {
      mapInstance.setZoom(4); // Zoom out for nationwide view
    } else if (selectedGeoLevel === 'county') {
      mapInstance.setZoom(6); // Zoom in for county view
    } else if (selectedGeoLevel === 'blockgroup') {
      mapInstance.setZoom(11); // Zoom in more for block group view
    }
  }, [isActive, selectedGeoLevel, mapInstance]);

  // Separate effect for handling polygon clicks - only center, don't zoom
  useEffect(() => {
    if (!mapInstance || !isActive || !clickedPolygonInfo || !clickedPolygonInfo.object) return;
    
    const feature = clickedPolygonInfo.object;
    const properties = feature.properties || {};
    
    // Try to get coordinates from the feature and center on it
    if (feature.geometry && feature.geometry.coordinates) {
      let center;
      
      if (feature.geometry.type === 'MultiPolygon') {
        // For MultiPolygon, use the first polygon's center
        const coords = feature.geometry.coordinates[0][0];
        center = {
          lat: coords[0][1], // First coordinate's lat
          lng: coords[0][0]  // First coordinate's lng
        };
      } else if (feature.geometry.type === 'Polygon') {
        // For Polygon, calculate center from coordinates
        const coords = feature.geometry.coordinates[0];
        let sumLat = 0, sumLng = 0;
        
        for (const [lng, lat] of coords) {
          sumLat += lat;
          sumLng += lng;
        }
        
        center = {
          lat: sumLat / coords.length,
          lng: sumLng / coords.length
        };
      }
      
      if (center) {
        mapInstance.panTo(center);
      }
    }
  }, [isActive, clickedPolygonInfo, mapInstance]);

  if (!visible || !isActive) return null;

  // If no polygon is clicked, show level-specific placeholder
  if (!clickedPolygonInfo || !clickedPolygonInfo.object) {
      let placeholderText = '';
      if (selectedGeoLevel === 'nationwide') {
        placeholderText = 'Click on a state';
      } else if (selectedGeoLevel === 'blockgroup') {
        placeholderText = 'Click on a block group ';
      } else if (selectedGeoLevel === 'county') {
        placeholderText = 'Click on a county';
      }
      
    return (
      <StyledPaper elevation={3}>
        <Typography variant="subtitle2" color="white" fontWeight="600">
          {placeholderText}
        </Typography>
        
        {/* Navigation Buttons - Always show when at county level */}
        <Box mt={2} display="flex" gap={1}>
          {selectedGeoLevel !== 'nationwide' ? (
            // Show back button when at county or block group level
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={() => onGoBack && onGoBack()}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                },
                textTransform: 'none',
                fontWeight: '500'
              }}
            >
              ← Back
            </Button>
          ) : null}
        </Box>
      </StyledPaper>
    );
  }

  // Copy the tooltip logic here
  const feature = clickedPolygonInfo.object;
  const properties = feature.properties || {};
  
  // Get state name and demographic data
  const stateName = properties.NAME || 'Unknown State';
  const stateFips = properties.STATEFP || 'Unknown State FIPS';

  const countyName = properties.NAME || properties.COUNTY_NAME || 'Unknown County';
  const countyFips = properties.COUNTYFP || 'Unknown County FIPS';

  const blockGroupFips = stateFips + '_' + countyFips

  const selectedValue = properties[selectedDemographic] || 0;
  
  // Get all demographic percentages
  const demographics = {
    'White': properties.white_percentage || 0,
    'Black': properties.black_percentage || 0,
    'Hispanic': properties.hispanic_percentage || 0,
    'Asian': properties.asian_percentage || 0,
    'Mixed': properties.mixed_percentage || 0,
    'Other': properties.other_percentage || 0
  };
  
  // Get color for selected demographic
  const getColor = (demographic) => {
    const colors = {
      'hispanic_percentage': '#ff0000',
      'white_percentage': '#0096ff',
      'black_percentage': '#9600ff',
      'asian_percentage': '#00ff00',
      'mixed_percentage': '#ff6400',
      'total_population': '#ffff00'
    };
    return colors[selectedDemographic] || '#0096ff';
  };
  
  return (
    <StyledPaper elevation={3}>
    {/* Title and Divider - Only show on nationwide and county levels */}
    {selectedGeoLevel !== 'blockgroup' && (
      <>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="subtitle1" fontWeight="600" color="white" sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
            {selectedGeoLevel === 'nationwide' ? stateName : selectedGeoLevel === 'county' ? countyName : ''}
          </Typography>
          {isLoading && (
            <CircularProgress 
              size={16} 
              sx={{ color: 'white' }}
            />
          )}
        </Box>
        
        <Divider sx={{ mb: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
      </>
    )}
            
      {/* Selected demographic highlight */}
      <Box mb={2}>
        {/* <Typography variant="body2" color="rgba(255, 255, 255, 0.8)" mb={1}>
          Selected Demographic:
        </Typography> */}
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            width={12}
            height={12}
            borderRadius="50%"
            sx={{ backgroundColor: getColor(selectedDemographic) }}
          />
          <Typography variant="body1" color="white" fontWeight="600">
            {selectedDemographic.replace('_percentage', '').replace(/_/g, ' ').toUpperCase()}: {selectedValue.toFixed(1)}%
          </Typography>
        </Box>
      </Box>
      
      {/* All demographics breakdown */}
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Typography variant="body2" color="rgba(255, 255, 255, 0.8)" mb={1}>
          All Demographics:
        </Typography>
        {Object.entries(demographics).map(([race, percentage]) => (
          <Box key={race} display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="rgba(255, 255, 255, 0.9)">
              {race}:
            </Typography>
            <Typography variant="caption" color="white" fontWeight="500">
              {percentage.toFixed(1)}%
            </Typography>
          </Box>
        ))}
      </Box>
      
      {/* Navigation Buttons */}
      <Box mt={2} display="flex" gap={1} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
        {selectedGeoLevel !== 'nationwide' ? (
          // Show back button when at county or block group level
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={() => onGoBack && onGoBack()}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
              },
              textTransform: 'none',
              fontWeight: '500'
            }}
          >
            ← Back
          </Button>
        ) : (
          // Show county button when at nationwide level
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={() => onGoCountyLevel && onGoCountyLevel(stateFips)}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
              },
              textTransform: 'none',
              fontWeight: '500'
            }}
          >
            County Level
          </Button>
        )}
        
        {/* Show block group button when at county level */}
        {selectedGeoLevel !== 'nationwide' && selectedGeoLevel !== 'blockgroup' && (
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={() => onGoBlockGroupLevel && onGoBlockGroupLevel(blockGroupFips)}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
              },
              textTransform: 'none',
              fontWeight: '500'
            }}
          >
            Block Group
          </Button>
        )}
      </Box>
    </StyledPaper>
  );
};

export default DemographicsNavigator; 